/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import { normalizeThreadSandboxMode } from '@/lib/pieveComposerSettings.js';
import { EMPTY_ARRAY, THREAD_SANDBOX_MODE_DEFAULT } from '@/redux/selectors/pieveSelectors/constants.js';
import { threadContentMap, threadMetaById, workflowRunDailyCountsList, workflowRunList } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import getNormalizedThreadId from '@/redux/selectors/pieveSelectors/lib/getNormalizedThreadId.js';
import getThreadContentValue from '@/redux/selectors/pieveSelectors/lib/getThreadContentValue.js';
import getThreadMetaValue from '@/redux/selectors/pieveSelectors/lib/getThreadMetaValue.js';
import getThreadParentId from '@/redux/selectors/pieveSelectors/lib/getThreadParentId.js';
import getThreadProjectLabel from '@/redux/selectors/pieveSelectors/lib/getThreadProjectLabel.js';
import getThreadType from '@/redux/selectors/pieveSelectors/lib/getThreadType.js';
import { projectMetaById } from '@/redux/selectors/pieveSelectors/sidebarProjectSelectors.js';
import { activeThreadId } from '@/redux/selectors/pieveSelectors/activeThreadIdSelector.js';
import { authUserId } from '@/redux/selectors/userSelectors.js';

export const workflowRunsForThread = createSelector([workflowRunList, (_state, threadId) => getNormalizedThreadId(threadId)], (runs, normalizedThreadId) => {
  if (!normalizedThreadId) {
    return EMPTY_ARRAY;
  }

  const filteredRuns = runs.filter((run) => String(run?.threadId || '').trim() === normalizedThreadId);

  if (filteredRuns.length === 0) {
    return EMPTY_ARRAY;
  }

  return filteredRuns;
});

export const workflowRunDailyCountsForThread = createSelector([workflowRunDailyCountsList, (_state, threadId) => getNormalizedThreadId(threadId)], (rows, normalizedThreadId) => {
  if (!normalizedThreadId) {
    return EMPTY_ARRAY;
  }

  const filteredRows = rows.filter((row) => String(row?.threadId || '').trim() === normalizedThreadId);

  if (filteredRows.length === 0) {
    return EMPTY_ARRAY;
  }

  return filteredRows;
});

export const threadMeta = (state, threadId) => {
  return getThreadMetaValue({
    threadMetaByIdValue: threadMetaById(state),
    threadId
  });
};

export const threadType = (state, threadId) => {
  return getThreadType(threadMeta(state, threadId));
};

export const threadParentId = (state, threadId) => {
  return getThreadParentId(threadMeta(state, threadId));
};

export const activeThreadMeta = createSelector([threadMetaById, activeThreadId], (threadMetaByIdValue, activeThreadId) => {
  return getThreadMetaValue({
    threadMetaByIdValue,
    threadId: activeThreadId
  });
});

/* A workflow run executes as the thread's owner, so any surface offering to act on the run's behalf
   has to know whether the viewer is that owner. Read by the workflow rail's Connect action. */
export const isActiveThreadOwnedByViewer = createSelector([activeThreadMeta, authUserId], (threadMetaValue, viewerUserId) => {
  const ownerUserId = String(threadMetaValue?.userId || '').trim();

  return Boolean(ownerUserId) && ownerUserId === String(viewerUserId || '').trim();
});

export const activeThreadContent = createSelector([threadContentMap, activeThreadId], (threadContentById, activeThreadId) => {
  return getThreadContentValue({
    threadContentMapValue: threadContentById,
    threadId: activeThreadId
  });
});

export const threadProjectLabel = (state, threadId) => {
  return getThreadProjectLabel({
    threadMeta: threadMeta(state, threadId),
    projectMetaById: projectMetaById(state)
  });
};

export const threadFilePreview = (state, threadId) => {
  return threadContent(state, threadId)?.filePreview || null;
};

export const activeThreadFilePreview = createSelector([activeThreadId, threadContentMap], (activeThreadId, threadContentById) => {
  return (
    getThreadContentValue({
      threadContentMapValue: threadContentById,
      threadId: activeThreadId
    })?.filePreview || null
  );
});

export const activeThreadParentId = createSelector([activeThreadMeta], (threadMetaValue) => {
  return getThreadParentId(threadMetaValue);
});

export const threadSandboxMode = (state, threadId) => {
  if (!getNormalizedThreadId(threadId)) {
    return THREAD_SANDBOX_MODE_DEFAULT;
  }

  return normalizeThreadSandboxMode(threadMeta(state, threadId)?.sandboxMode);
};

export const threadBranchName = (state, threadId) => {
  return String(threadMeta(state, threadId)?.branchName || '').trim();
};

export const threadContent = (state, threadId) => {
  const content = threadContentMap(state);

  if (threadId === undefined) {
    return content;
  }

  return getThreadContentValue({
    threadContentMapValue: content,
    threadId
  });
};

export const threadComposer = (state, threadId) => {
  return threadContent(state, threadId)?.composer || null;
};

export const threadAppConnections = (state, threadId) => {
  return threadContent(state, threadId)?.appConnections || EMPTY_ARRAY;
};

export const threadExists = (state, threadId) => {
  if (!getNormalizedThreadId(threadId)) {
    return false;
  }

  return Boolean(threadMeta(state, threadId) || threadContent(state, threadId));
};

export const threadContextWindowUsage = (state, threadId) => {
  return threadContent(state, threadId)?.contextWindowUsage || null;
};

export const threadDiffCard = (state, threadId) => {
  return threadContent(state, threadId)?.diffCard || null;
};

export const threadToolCallEvents = (state) => state?.pieve?.threadToolCallEvents || EMPTY_ARRAY;
